import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import App from './App.vue'
import router from './router'
import { isTenantDetailRoute } from './router/routeViewKey'
import { i18n } from './i18n'
import { queryClient } from './lib/queryClient'
import { loggerService, toLoggableError } from './services/logger.service'
import {
  beforeSendSentryEvent,
  sentryReplayOptions,
  sentryReplaysOnErrorSampleRate,
} from './utils/sentryConfig'
import {
  clearStaleChunkReloadFlag,
  registerStaleChunkRecoveryHandlers,
  reloadForStaleChunk,
} from './utils/staleChunkRecovery'
import './style.css'
import 'vue-sonner/style.css'
import '@fontsource/inter/latin-400.css'
import '@fontsource/inter/latin-500.css'
import '@fontsource/inter/latin-600.css'
import '@fontsource/ibm-plex-sans-arabic/arabic-400.css'
import '@fontsource/ibm-plex-sans-arabic/arabic-600.css'

const loc = i18n.global.locale as unknown as { value: string }
const initial = loc.value as 'en' | 'ar'
document.documentElement.lang = initial
document.documentElement.dir = initial === 'ar' ? 'rtl' : 'ltr'

registerStaleChunkRecoveryHandlers()

const app = createApp(App)
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(VueQueryPlugin, { queryClient })

app.config.errorHandler = (err, _instance, info) => {
  void loggerService.logError(
    toLoggableError(err),
    {
      action: 'vue_error',
      route: router.currentRoute.value.path
    }
  )

  if (import.meta.env.DEV) {
    console.error('Global error:', err, info)
  }
}

window.addEventListener('unhandledrejection', (event) => {
  if (reloadForStaleChunk(event.reason, 'unhandled-rejection')) {
    event.preventDefault()
    return
  }

  void loggerService.logError(
    toLoggableError(event.reason),
    { action: 'unhandled_promise_rejection' }
  )

  if (import.meta.env.PROD) {
    event.preventDefault()
  }
})

window.addEventListener('error', (event) => {
  void loggerService.logError(
    event.error instanceof Error ? event.error : new Error(event.message || 'Unknown error'),
    {
      action: 'window_error',
      route: window.location.pathname
    }
  )
})

// Initialize Sentry if DSN is configured
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || 'development',
    release: import.meta.env.VITE_APP_VERSION,
    sendDefaultPii: false,
    attachErrorHandler: false,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(sentryReplayOptions),
    ],
    tracesSampleRate: import.meta.env.VITE_APP_ENV === 'production' ? 0.1 : 0.0,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: sentryReplaysOnErrorSampleRate,
    beforeSend: beforeSendSentryEvent,
  })
}

app.mount('#app')
router
  .isReady()
  .then(() => {
    // On tenant detail pages, TenantDetailLayout eagerly prefetches its tab
    // chunks right after mount. Those speculative imports settle AFTER this
    // point, so clearing the reload guard here would let a stale prefetched
    // chunk (404 after a deploy / permanently missing / offline cached shell)
    // re-arm the guard on every reload and cause an endless reload loop.
    // prefetchTenantTabComponents() keeps the guard armed while it settles and
    // clears it once done, so a real navigation to a stale tab still gets the
    // single-reload recovery.
    if (!isTenantDetailRoute(router.currentRoute.value)) {
      clearStaleChunkReloadFlag()
    }
  })
  .catch((err: unknown) => {
    // A rejected lazy chunk during initial load is handled by the stale-chunk
    // recovery (it may have already triggered a reload); otherwise report it.
    if (!reloadForStaleChunk(err, 'router-ready')) {
      void loggerService.logError(toLoggableError(err), { action: 'router_ready' })
    }
  })
